import { GetConfigureFormOperations, Operation } from '@occc-app/offerHelpers/types';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getLimitsInConstraintOperations, LimitType } from '@occc/feat-limits';
import { getFulfillmentWindowOperations } from '@occc/feat-constraints';
import {
  makeMarketingContentCreateOperation,
  makeMarketingContentUpdateOperation,
} from '@occc-app/offerHelpers/operations/marketingContent';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { Offer_Presentation_Type, OfferFragment, ReceiptContentInput } from '@occc/graphql';

import { getStoredValueEarningMechanismOperations } from './stored-value-earning-mechanism-operations';
import { StoredValueConfigureFormValues } from './stored-value-configure-form.types';
import { getStoredValueProgramOperations } from './stored-value-program-operations';

const getReceiptContentInput = (
  receiptContent: StoredValueConfigureFormValues['receiptContent'],
): ReceiptContentInput => ({
  shortDescription: receiptContent?.shortDescription || null,
  longDescription: receiptContent?.longDescription || null,
});

const getStoredValueMarketingContentOperation = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: StoredValueConfigureFormValues,
  dirtyFields: Parameters<GetConfigureFormOperations<StoredValueConfigureFormValues>>[3],
): Operation[] => {
  // promotionTitle (used in digital receipts) is derived from marketingContents.title on the backend;
  // stored value offers have no Design tab, so we sync the title manually from the program name.
  const existingMarketingContent = offer?.marketingContents?.[0];
  const receiptContent = getReceiptContentInput(values.receiptContent);

  if (!existingMarketingContent) {
    return [
      makeMarketingContentCreateOperation({
        offerId,
        title: values.name,
        receiptContent,
      }),
    ];
  }

  const isNameDirty = isFieldDirty<StoredValueConfigureFormValues>(dirtyFields, 'name');
  const isReceiptContentDirty = isFieldDirty<StoredValueConfigureFormValues>(
    dirtyFields,
    'receiptContent',
  );

  if (!isNameDirty && !isReceiptContentDirty) {
    return [];
  }

  return [
    makeMarketingContentUpdateOperation({
      id: existingMarketingContent.id,
      ...(isNameDirty ? { title: values.name } : {}),
      ...(isReceiptContentDirty ? { receiptContent } : {}),
    }),
  ];
};

export const getStoredValueConfigureFormOperations: GetConfigureFormOperations<
  StoredValueConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getStoredValueEarningMechanismOperations(
    offerId,
    values,
    dirtyFields,
  );
  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
  );
  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );
  const nameAndDescriptionOperations = getOfferNameAndDescriptionUpdateOperations(
    offerId,
    values,
    dirtyFields,
  );
  const marketingContentOperations = getStoredValueMarketingContentOperation(
    offerId,
    offer,
    values,
    dirtyFields,
  );
  const storedValueProgramOperations = getStoredValueProgramOperations(
    offerId,
    values,
    dirtyFields,
  );

  const additionalOperations: Operation[] = [];
  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }
  if (!offer || offer.presentationType !== Offer_Presentation_Type.Hidden) {
    additionalOperations.push(
      makeOfferUpdateOperation(offerId, {
        presentationType: Offer_Presentation_Type.Hidden, // always hide the Stored Value offers
      }),
    );
  }

  return [
    ...earningMechanismOperations,
    ...fulfillmentWindowOperations,
    ...awardCountLimitsOperations,
    ...nameAndDescriptionOperations,
    ...marketingContentOperations,
    ...additionalOperations,
    ...storedValueProgramOperations,
  ];
};
